import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";
import { Card, CardBody, CardHeader, Container, Row } from "shards-react";
import {
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import NumberFormat from "react-number-format";
import PageTitle from "../common/PageTitle";
import api from "../common/api";
import Chart from "../../utils/chart";

moment.locale("es");

const MIN_MONTH = "2025-03";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const getCurrentMonth = () => moment().format("YYYY-MM");

const getDefaultFromMonth = () => {
  const twelveAgo = moment()
    .subtract(11, "months")
    .format("YYYY-MM");
  return twelveAgo < MIN_MONTH ? MIN_MONTH : twelveAgo;
};

const normalizeMonth = value => {
  if (!value) return "";
  return String(value).slice(0, 7);
};

const clampMonth = value => {
  const current = getCurrentMonth();
  if (!value || value < MIN_MONTH) return MIN_MONTH;
  if (value > current) return current;
  return value;
};

const getMonthsInRange = (from, to) => {
  const months = [];
  const start = moment(from, "YYYY-MM");
  const end = moment(to, "YYYY-MM");
  if (!start.isValid() || !end.isValid() || start.isAfter(end, "month")) {
    return months;
  }
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, "month")) {
    months.push(cursor.format("YYYY-MM"));
    cursor.add(1, "month");
  }
  return months;
};

const formatMonthLabel = month => {
  const label = moment(month, "YYYY-MM").format("MMMM YYYY");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const formatShortMonthLabel = month => {
  const label = moment(month, "YYYY-MM").format("MMM YY");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const formatCurrency = value =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0
  }).format(value);

const parsePrice = value => {
  if (value === undefined || value === null || value === "") return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const extractList = payload => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
};

const extractVehicle = payload => {
  if (!payload) return {};
  if (Array.isArray(payload)) return payload[0] || {};
  if (payload.plate) return payload;
  const values = Object.values(payload);
  if (values[0] && typeof values[0] === "object" && values[0].plate) {
    return values[0];
  }
  return payload;
};

const VehiclePricingHistory = ({ match }) => {
  const vehicleId = match.params.id;
  const currentMonth = getCurrentMonth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fromMonth, setFromMonth] = useState(getDefaultFromMonth());
  const [toMonth, setToMonth] = useState(currentMonth);
  const [priceMap, setPriceMap] = useState({});
  const [existingMonths, setExistingMonths] = useState({});
  const [vehicle, setVehicle] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const chronologicalMonths = useMemo(
    () => getMonthsInRange(fromMonth, toMonth),
    [fromMonth, toMonth]
  );
  const months = useMemo(() => [...chronologicalMonths].reverse(), [
    chronologicalMonths
  ]);

  const missingCount = months.filter(
    month => priceMap[month] === undefined || priceMap[month] === ""
  ).length;

  const chartPoints = useMemo(
    () =>
      chronologicalMonths.map(month => ({
        month,
        price: parsePrice(priceMap[month])
      })),
    [chronologicalMonths, priceMap]
  );

  const pricedPoints = chartPoints.filter(point => point.price !== null);
  const firstPrice = pricedPoints.length ? pricedPoints[0].price : null;
  const lastPrice =
    pricedPoints.length > 1
      ? pricedPoints[pricedPoints.length - 1].price
      : firstPrice;
  const priceVariation =
    firstPrice && lastPrice && firstPrice !== 0
      ? ((lastPrice - firstPrice) / firstPrice) * 100
      : null;

  useEffect(() => {
    if (!canvasRef.current) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    chartRef.current = new Chart(canvasRef.current, {
      type: "line",
      data: {
        labels: chartPoints.map(point => formatShortMonthLabel(point.month)),
        datasets: [
          {
            label: "Precio",
            fill: "start",
            data: chartPoints.map(point => point.price),
            backgroundColor: "rgba(0,123,255,0.1)",
            borderColor: "rgba(0,123,255,1)",
            pointBackgroundColor: "#ffffff",
            pointHoverBackgroundColor: "rgb(0,123,255)",
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            spanGaps: true
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        legend: {
          display: false
        },
        elements: {
          line: {
            tension: 0.3
          }
        },
        scales: {
          xAxes: [
            {
              gridLines: false,
              ticks: {
                fontColor: "#6c757d",
                maxRotation: 0,
                autoSkip: true
              }
            }
          ],
          yAxes: [
            {
              ticks: {
                fontColor: "#6c757d",
                beginAtZero: false,
                callback(tick) {
                  if (tick >= 1000000) {
                    return `$${(tick / 1000000).toFixed(1)}M`;
                  }
                  if (tick >= 1000) {
                    return `$${(tick / 1000).toFixed(0)}K`;
                  }
                  return `$${tick}`;
                }
              }
            }
          ]
        },
        hover: {
          mode: "index",
          intersect: false
        },
        tooltips: {
          custom: false,
          mode: "index",
          intersect: false,
          callbacks: {
            title(tooltipItems) {
              const index = tooltipItems[0] && tooltipItems[0].index;
              if (index === undefined || !chartPoints[index]) return "";
              return formatMonthLabel(chartPoints[index].month);
            },
            label(tooltipItem) {
              if (tooltipItem.yLabel === null || tooltipItem.yLabel === undefined) {
                return "Sin precio";
              }
              return formatCurrency(tooltipItem.yLabel);
            }
          }
        }
      }
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [chartPoints, loading]);

  const loadData = async () => {
    const token = localStorage.getItem("userToken");
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    try {
      const [historyRes, vehicleRes] = await Promise.all([
        axios.get(api.vehicles().pricingHistory(vehicleId), { headers }),
        axios.get(api.vehicles().get(vehicleId), { headers })
      ]);
      const list = extractList(historyRes.data);
      const vehicleData = extractVehicle(vehicleRes.data);
      const nextPrices = {};
      const nextExisting = {};
      list.forEach(item => {
        const month = normalizeMonth(item.month);
        if (!month) return;
        nextPrices[month] =
          item.price === null || item.price === undefined ? "" : item.price;
        nextExisting[month] = true;
      });
      setPriceMap(nextPrices);
      setExistingMonths(nextExisting);
      setVehicle({
        plate: (list[0] && list[0].plate) || vehicleData.plate || "",
        description:
          (list[0] && list[0].description) ||
          vehicleData.description ||
          (vehicleData.version_full_name &&
            vehicleData.version_full_name.name) ||
          "",
        codia: (list[0] && list[0].codia) || ""
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "No se pudo cargar el historial de precios",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  const handleFromChange = event => {
    const value = clampMonth(event.target.value);
    setFromMonth(value > toMonth ? toMonth : value);
  };

  const handleToChange = event => {
    const value = clampMonth(event.target.value);
    setToMonth(value < fromMonth ? fromMonth : value);
  };

  const handlePriceChange = (month, value) => {
    setPriceMap(prev => ({
      ...prev,
      [month]: value
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const prices = months
      .filter(month => priceMap[month] !== undefined && priceMap[month] !== "")
      .map(month => ({
        month,
        price: priceMap[month]
      }));

    if (prices.length === 0) {
      setSnackbar({
        open: true,
        message: "Ingrese al menos un precio para guardar",
        severity: "error"
      });
      return;
    }

    const token = localStorage.getItem("userToken");
    setSaving(true);
    try {
      await axios.post(
        api.vehicles().storePricingHistory(vehicleId),
        {
          vehicle_id: Number(vehicleId),
          prices: prices.map(item => ({
            month: item.month,
            price: Number(item.price)
          }))
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSnackbar({
        open: true,
        message: "Precios históricos actualizados con éxito",
        severity: "success"
      });
      await loadData();
    } catch (err) {
      const errors = err.response && err.response.data && err.response.data.errors;
      let message = "No se pudieron guardar los precios";
      if (errors) {
        message = Object.values(errors)
          .map(value => (Array.isArray(value) ? value[0] : value))
          .join(" ");
      } else if (err.response && err.response.data && err.response.data.message) {
        message = err.response.data.message;
      }
      setSnackbar({
        open: true,
        message,
        severity: "error"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container fluid className="main-content-container px-4 pb-4">
      <Row noGutters className="page-header pt-4">
        <PageTitle
          lg="12"
          title="Admin de precios históricos"
          subtitle={
            vehicle.plate
              ? `${vehicle.plate}${
                  vehicle.description ? " - " + vehicle.description : ""
                }${vehicle.codia ? " - CODIA " + vehicle.codia : ""}`
              : "Vehículo"
          }
        />
      </Row>
      {loading ? (
        <div className="d-flex justify-content-center">
          <CircularProgress />
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="row mt-3">
            <div className="col-md-3">
              <TextField
                id="from_month"
                name="from_month"
                type="month"
                margin="normal"
                fullWidth
                inputProps={{ min: MIN_MONTH, max: currentMonth }}
                value={fromMonth}
                onChange={handleFromChange}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="caption">Desde</Typography>
                    </InputAdornment>
                  ),
                  style: { fontSize: "0.75rem" }
                }}
              />
            </div>
            <div className="col-md-3">
              <TextField
                id="to_month"
                name="to_month"
                type="month"
                margin="normal"
                fullWidth
                inputProps={{ min: MIN_MONTH, max: currentMonth }}
                value={toMonth}
                onChange={handleToChange}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="caption">Hasta</Typography>
                    </InputAdornment>
                  ),
                  style: { fontSize: "0.75rem" }
                }}
              />
            </div>
          </div>
          <div className="mt-2 mb-2">
            {missingCount > 0 ? (
              <Chip
                size="small"
                color="secondary"
                label={`Faltan ${missingCount} precio${
                  missingCount === 1 ? "" : "s"
                } de hoy hacia atrás`}
              />
            ) : (
              <Chip
                size="small"
                color="primary"
                label="Todos los meses del período tienen precio"
              />
            )}
          </div>
          <div className="mb-3 row">
            <Table className="col-md-6">
              <TableHead>
                <TableRow>
                  <TableCell className="p-1">Mes</TableCell>
                  <TableCell className="p-1">Precio</TableCell>
                  <TableCell className="p-1">Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {months.map(month => {
                  const hasPrice =
                    priceMap[month] !== undefined && priceMap[month] !== "";
                  const isMissing = !hasPrice;
                  return (
                    <TableRow
                      key={month}
                    >
                      <TableCell className="p-1">
                        {formatMonthLabel(month)}
                      </TableCell>
                      <TableCell className="p-1" style={{ minWidth: 180 }}>
                        <NumberFormat
                          id={`price_${month}`}
                          name={`price_${month}`}
                          value={
                            priceMap[month] === undefined ? "" : priceMap[month]
                          }
                          onValueChange={values => {
                            handlePriceChange(month, values.value);
                          }}
                          thousandSeparator="."
                          decimalSeparator=","
                          prefix="$"
                          allowNegative={false}
                          customInput={TextField}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            style: { fontSize: "0.75rem" }
                          }}
                        />
                      </TableCell>
                      <TableCell className="p-1">
                        {isMissing ? (
                          <Chip size="small" label="Faltante" color="secondary" />
                        ) : existingMonths[month] ? (
                          <Chip size="small" label="Cargado" style={{ backgroundColor: "#187C19", color: "#fff" }} />
                        ) : (
                          <Chip size="small" label="Nuevo" style={{ backgroundColor: "#187C19", color: "#fff" }} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Card small className="col-md-6" style={{ height: "fit-content" }}>
              <CardHeader className="border-bottom d-flex align-items-center justify-content-between">
                <h6 className="m-0">Tendencia de precios</h6>
                {priceVariation !== null && pricedPoints.length > 1 && (
                  <Chip
                    size="small"
                    color={priceVariation >= 0 ? "primary" : "secondary"}
                    label={`${priceVariation >= 0 ? "+" : ""}${priceVariation.toFixed(
                      1
                    )}% en el período`}
                  />
                )}
              </CardHeader>
              <CardBody className="pt-3">
                {pricedPoints.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    Cargá al menos un precio para ver la tendencia.
                  </Typography>
                ) : (
                  <canvas
                    height="90"
                    ref={canvasRef}
                    style={{ maxWidth: "100%" }}
                  />
                )}
              </CardBody>
            </Card>
          </div>
          <div style={{ textAlign: "right" }}>
            <Button
              variant="contained"
              className="mt-3 ml-1 mr-1"
              color="secondary"
              onClick={() => window.history.back()}
              disabled={saving}
            >
              Volver
            </Button>
            <Button
              variant="contained"
              className="mt-3 ml-1 mr-1"
              color="primary"
              type="submit"
              disabled={saving}
            >
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </form>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default VehiclePricingHistory;
